import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import useTranslation from "../../../../../../packages/locales/translations/util/useTranslation";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { LoadMore } from "@coworker/apprestructured/src/shared/components/LoadMore/LoadMore";
import { useCallback, useContext, useMemo } from "react";
import { ModalContext } from "@coworker/apprestructured/src/layout/context/ModalContextProvider";
import { ModalType } from "@coworker/apprestructured/src/layout/context/modalType";
import { useItemsInfoQueries } from "@coworker/apprestructured/src/shared/hooks/item/useItemsInfo";
import { TaskCardRenderer } from "../TaskCardRenderer";
import { MediaTaskListItem } from "../types";
import { mapItemsByNo } from "../utils";
import styles from "./media-tasks-modal.module.css";

interface MediaTasksModalProps {
  showModal: boolean;
  onClose: () => void;
  tasks: MediaTaskListItem[];
  totalCount?: number;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
}

export const MediaTasksModal = ({
  showModal,
  onClose,
  tasks,
  totalCount,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: MediaTasksModalProps) => {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParamState();
  const { toggleModal } = useContext(ModalContext);

  // Get item info for V1 tasks
  const itemNos = useMemo(() => {
    return tasks
      .filter((task) => task.type === "v1")
      .map((task) => task.task.product_article_id)
      .filter(Boolean) as string[];
  }, [tasks]);

  const itemsInfoResult = useItemsInfoQueries(itemNos);
  const itemsInfo = useMemo(() => {
    return itemsInfoResult
      .map((result) => result.data)
      .filter((item): item is NonNullable<typeof item> => Boolean(item));
  }, [itemsInfoResult]);

  // Map items by number for O(1) lookup
  const itemsMap = useMemo(() => mapItemsByNo(itemsInfo), [itemsInfo]);

  const handleV1TaskClick = useCallback(
    (taskId: string, taskType: string) => {
      toggleModal({
        type: ModalType.TaskDetail,
        payload: { taskId, taskType },
      });
    },
    [toggleModal]
  );

  const handleTaskClick = useCallback(
    (taskListItem: MediaTaskListItem) => {
      if (taskListItem.type === "v1") {
        // V1 tasks: Use old modal
        toggleModal({
          type: ModalType.TaskDetail,
          payload: {
            taskId: taskListItem.task.id,
            taskType: taskListItem.task.task_type,
          },
        });
      } else {
        // V2 tasks: Use new TaskViewerSheet
        setSearchParams((params) => {
          params.set("taskViewer", String(taskListItem.task.id));
        });
      }
    },
    [toggleModal, setSearchParams]
  );

  if (!showModal) return null;

  const headerTitle =
    totalCount !== undefined && totalCount > 0
      ? `${t("TasksString")} (${tasks.length}/${totalCount})`
      : t("TasksString");

  return (
    <FixaModal visible={showModal} handleCloseBtn={onClose}>
      <FixaSheets
        ariaLabel={t("TasksString")}
        size="large"
        header={<FixaModalHeader title={headerTitle} closeBtnClick={onClose} />}
        footer={null}
      >
        <FixaModalBody>
          <ul className={styles["task-list"]}>
            {tasks.map((taskListItem) => (
              <TaskCardRenderer
                key={taskListItem.task.id}
                taskListItem={taskListItem}
                itemsMap={itemsMap}
                onV1TaskClick={handleV1TaskClick}
                onV2TaskClick={handleTaskClick}
              />
            ))}
          </ul>
          {hasNextPage && onLoadMore && (
            <LoadMore
              totalCount={totalCount}
              loadedItemsCount={tasks.length}
              onLoadMoreClick={onLoadMore}
              isFetching={isFetchingNextPage}
            />
          )}
        </FixaModalBody>
      </FixaSheets>
    </FixaModal>
  );
};
